import router from "@/router"
import { AdversaryApi } from "@/services/adversaries-service"
import { useSystemFeedback } from "@/stores/systemFeedback"
import type {
  AdversaryGet,
  AdversaryId,
  AdversaryPost
} from "@/types/adversaries"
import { defineStore } from "pinia"
import type { ApiError } from "@/types/api"
import type { Pagination } from "@/types/pagination"

export const useAdversaries = defineStore("adversaries", {
  state: () => {
    return {
      listAllAdversaries: {} as Pagination<AdversaryGet>,
      listFilteredAdversaries: {} as Pagination<AdversaryGet>,
      currentAdversary: {} as AdversaryGet,
      currentPage: 1 as number,
      api: new AdversaryApi()
    }
  },

  actions: {
    async getAllAdversaries(
      page: number,
      search?: string,
      order?: string,
      per_page?: number
    ) {
      try {
        const response = await this.api.getAllAdversaries(
          page,
          search,
          per_page,
          order
        )
        this.listAllAdversaries = response.data
        this.listFilteredAdversaries = response.data
        return response
      } catch (error) {
        this.listAllAdversaries = {} as Pagination<AdversaryGet>
        this.listFilteredAdversaries = {} as Pagination<AdversaryGet>
        this.handleErrors(error as ApiError)
      }
    },

    async getAdversary(id: AdversaryId) {
      this.currentAdversary = {} as AdversaryGet
      try {
        const response = await this.api.getAdversary(id)
        this.currentAdversary = response.data
        return response
      } catch (error) {
        this.handleErrors(error as ApiError)
      }
    },

    async createAdversary(data: AdversaryPost) {
      try {
        const response = await this.api.createAdversary(data)
        this.handleSuccessMessage(response.data.detail)
        router.push({ name: "adversariesList" })
        return response
      } catch (error) {
        this.handleErrors(error as ApiError)
      }
    },

    async validateAdversary(data: AdversaryPost) {
      try {
        const response = await this.api.validateAdversary(data)
        return response
      } catch (error) {
        this.handleErrors(error as ApiError)
      }
    },

    async updateAdversary(id: AdversaryId, data: AdversaryPost) {
      try {
        const response = await this.api.updateAdversary(id, data)
        this.handleSuccessMessage(response.data.detail)
        return response
      } catch (error) {
        this.handleErrors(error as ApiError)
      }
    },

    async deleteAdversary(id: AdversaryId) {
      try {
        const response = await this.api.deleteAdversary(id)
        this.handleSuccessMessage(response.data.detail)
        return response
      } catch (error) {
        this.handleErrors(error as ApiError)
      }
    },

    handleSuccessMessage(message: string) {
      useSystemFeedback().setFeedback({
        type: "success",
        text: `backend.${message}`
      })
    },

    handleErrors(error: ApiError) {
      const errorMessage = error?.response
        ? `backend.${error.response.data.detail}`
        : "requestError.default"
      useSystemFeedback().setFeedback({ type: "danger", text: errorMessage })
    }
  }
})
